import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DeliveryOrderStatusCodes } from '@common/knownTypes/DeliveryOrderStatusCodes';
import { CodelistService } from '@common/services/codelist.service';
import { DeliveryOrderService } from '@common/services/delivery-order.service';
import { ModalService } from '@common/services/modal.service';
import { requiredValidator } from '@common/validators/required-validator';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { DeliveryOrderListDisplayModel } from 'app/vessel-visit/delivery-order-list/models/DeliveryOrders.display.model';
import { catchError, first, Observable, of, tap, throwError } from 'rxjs';

@Component({
    selector: 'app-edit-delivery-order',
    templateUrl: './edit-delivery-order.component.html',
    styleUrls: ['./edit-delivery-order.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditDeliveryOrderComponent implements OnInit, AfterViewInit, AfterContentChecked {
    isBusy: boolean;
    deliveryOrderModel: DeliveryOrderListDisplayModel = new DeliveryOrderListDisplayModel();
    form: UntypedFormGroup;
    deliveryOrderId: number;
    vesselVisitId: number;
    terminalId: string;
    portId: string;
    berthId: string;
    vesselMaster: string;
    relationTerminalOptions$: Observable<any>;
    berths$: Observable<any>;
    systems$: Observable<any>;
    vesselVisits$: Observable<any>;
    serviceProviders$: Observable<any>;

    itemArrayRef: UntypedFormArray;

    constructor(
        public dialogService: ModalService,
        public activeModal: DialogRef,
        public service: DeliveryOrderService,
        private cdr: ChangeDetectorRef,
        public codelistService: CodelistService
    ) {}

    ngOnInit() {
        this.form = this.deliveryOrderModel.generateGroup();

        this.itemArrayRef = this.form.get('items') as UntypedFormArray;

        this.form.get('id').setValue(this.deliveryOrderId);

        // when creating from DO tab on VV edit, we have vesselVisitId
        if (this.vesselVisitId) {
            this.form.get('vesselVisitId').setValue(this.vesselVisitId);
            this.loadVesselVisitDependencies(this.vesselVisitId);
        } else {
            this.vesselVisits$ = this.codelistService.getVesselVisitFilteredOptions();
        }
    }

    ngAfterViewInit() {
        this.trackFormChanges();
        if (this.deliveryOrderModel?.status?.id === DeliveryOrderStatusCodes.created) {
            this.form.get('serviceProviderId').disable();
        }
        this.cdr.detectChanges();
    }

    ngAfterContentChecked() {
        this.cdr.detectChanges();
    }

    addNewDeliveryOrder() {
        const vesselVisitId = this.vesselVisitId || this.form.get('vesselVisitId').value;
        const body = { vesselVisitId, ...this.form.getRawValue() };

        if (body.items?.length > 0) {
            body.items = body.items.map((item) => ({
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                grossWeight: item.grossWeight,
                netWeight: item.netWeight,
                volume: item.volume,
                volumeUnitOfMeasureId: item.volumeUnitOfMeasureId
            }));
        }

        this.service
            .addDeliveryOrder(body)
            .pipe(
                first(),
                catchError((err) => {
                    this.isBusy = false;
                    return throwError(err);
                })
            )
            .subscribe((data) => {
                this.isBusy = false;
                this.activeModal.close(data);
            });
    }

    onVesselVisitChange(vesselVisitId) {
        this.loadVesselVisitDependencies(vesselVisitId);
    }

    addItem() {
        this.itemArrayRef.push(this.createItemGroup());
    }

    removeItem(index: number) {
        this.itemArrayRef.removeAt(index);
    }

    private trackFormChanges() {
        this.form.get('relationSystemToOrganizationToTerminalId').valueChanges.subscribe((relationId) => {
            if (relationId) {
                this.berths$ = this.service.getBerthsBasedOnTerminal(relationId).pipe(
                    tap(() => {
                        this.form.get('berthId').setValue(this.berthId);
                    })
                );
            } else {
                this.form.get('berthId').setValue(null);
                this.berths$ = of([]);
            }
        });
        if (this.vesselMaster) this.form.get('vesselMaster').setValue(this.vesselMaster);
    }

    private loadVesselVisitDependencies(vesselVisitId: number) {
        if (!vesselVisitId) {
            this.form.get('relationSystemToOrganizationToTerminalId').setValue(null);
            this.relationTerminalOptions$ = of([]);
            this.serviceProviders$ = of([]);
            return;
        }
        this.relationTerminalOptions$ = this.service.getRelationTerminalOptionsOnDeliveryOrder(vesselVisitId).pipe(
            tap((res) => {
                // Perform client-side filtering to avoid an additional granular API call.
                // If we find exactly one relation matching the terminalId, auto-select it.
                if (this.terminalId && res) {
                    const matches = res.filter((relation) => relation.name?.startsWith(this.terminalId));
                    if (matches.length === 1) {
                        this.form.get('relationSystemToOrganizationToTerminalId').setValue(matches[0].id);
                    }
                }
            })
        );
        this.serviceProviders$ = this.service.getServiceProvidersOnDeliveryOrder(vesselVisitId);
    }

    private createItemGroup(): UntypedFormGroup {
        return new UntypedFormGroup({
            name: new UntypedFormControl(null, requiredValidator()),
            description: new UntypedFormControl(null),
            quantity: new UntypedFormControl(null),
            grossWeight: new UntypedFormControl(null),
            netWeight: new UntypedFormControl(null),
            volume: new UntypedFormControl(null),
            volumeUnitOfMeasureId: new UntypedFormControl(null)
        });
    }
}
